import * as React from 'react';
import * as Yup from 'yup';
import { isValid } from 'date-fns';
import { Form } from 'formik';

import { MuiForm } from 'lib/Form';
import { isDefined } from 'lib/typeguards';
import { Option } from 'lib/types';
import { AwardTemplate } from 'components/customers/models/Award';
import { LoyaltyScope } from 'components/loyalty/enums';
import { IAwardEnrichmentDateTimeRestriction } from 'components/loyalty/models/AwardEnrichment';
import { IPerkBasketQualification, PerkDiscount } from 'components/loyalty/models/PerkModel';
import { getPerkDiscountScopeOptions, showChildItemRulePerkForm } from 'components/perks/helpers';
import { usePepperPerksRedemptionFormData } from 'components/perks/hooks/pepper-perks/usePepperPerksRedemptionFormData';
import { PerkFormStep } from 'components/perks/PerksEditStepper';
import { RedemptionFormCardContent } from './RedemptionFormContent';

interface PerkRedemptionFormProps {
    initialData?: AwardRedemptionFormData;
    isEdit?: boolean;
    isLoading?: boolean;
    currencySymbol: string;
    perkType: string;
    auto?: boolean;
    onPrevious?: (isValid?: boolean, values?: AwardRedemptionFormData) => void;
    clickPrevious: () => void;
    onUpdate?: (values: { type: PerkFormStep.REDEMPTION; values: AwardRedemptionFormData }) => void;
    onSubmit: (data: AwardRedemptionFormData) => void;
}

export interface AwardRedemptionFormData {
    token?: string;
    discount?: Partial<Omit<PerkDiscount, 'maxValue'>>;
    basketQualification?: Omit<IPerkBasketQualification, 'scope'> & {
        scope?: LoyaltyScope;
    };
    dateTimeRestriction: Omit<IAwardEnrichmentDateTimeRestriction, 'startTime' | 'daysOfWeek' | 'endTime'> & {
        startTime: string | undefined;
        endTime: string | undefined;
        daysOfWeek: string[];
    };
}

// this is UI only fields, do not forget to exclude them from the request
export interface AwardRedemptionFullFormData extends AwardRedemptionFormData {
    discount: AwardRedemptionFormData['discount'] & {
        groupedScopeMatchCodes: string[];
        allowQualifierDiscount: boolean;
    };
    dateTimeRestriction: AwardRedemptionFormData['dateTimeRestriction'] & {
        dateTimeRestictionOption: DateTimeOptionValue[];
    };
    basketQualification: AwardRedemptionFormData['basketQualification'] & {
        disableBasketQualification: boolean;
    };
}

const dateAndTimeRestrictionOptions: Option[] = [
    {
        value: 'date',
        label: 'specific start and end date'
    },
    {
        value: 'time',
        label: 'specific start and end time'
    },
    {
        value: 'day',
        label: 'particular weekday'
    }
];
export type DateTimeOptionValue = (typeof dateAndTimeRestrictionOptions)[number]['value'];

export const awardRedemptionDefaultFormValues: AwardRedemptionFullFormData = {
    token: '',
    discount: {
        scope: undefined,
        scopeMatchCode: undefined,
        value: undefined,
        rule: undefined,
        childItemRule: undefined,
        type: undefined,
        maxCount: 1,
        groupedScopeMatchCodes: [''],
        allowQualifierDiscount: false
    },
    basketQualification: {
        scope: undefined,
        matchCode: undefined,
        minimumItems: undefined,
        minimumValue: undefined,
        disableBasketQualification: true
    },
    dateTimeRestriction: {
        startDate: undefined,
        startTime: undefined,
        endDate: undefined,
        endTime: undefined,
        daysOfWeek: [],
        dateTimeRestictionOption: []
    }
};

const getAwardRedemptionDefaultFormValues = (perkType: string, isIbsAndAuto?: boolean) => {
    if (isIbsAndAuto) {
        return {
            dateTimeRestriction: {
                ...awardRedemptionDefaultFormValues.dateTimeRestriction
            }
        };
    }
    if (perkType === AwardTemplate.PSEUDO_CURRENCY) {
        return {
            ...awardRedemptionDefaultFormValues,
            discount: {
                ...awardRedemptionDefaultFormValues.discount,
                value: 0.01,
                type: 'VALUE'
            }
        };
    }
    return awardRedemptionDefaultFormValues;
};

const discountValidationSchema = (posProvider: string, perkType: string) =>
    Yup.object().shape({
        value: Yup.string().required('This field is required'),
        type: Yup.mixed().oneOf(['VALUE', 'PERCENTAGE', 'OVERRIDE']).required('This field is required'),
        scope: Yup.mixed()
            .oneOf(getPerkDiscountScopeOptions(posProvider).map(option => option.value))
            .required('This field is required'),
        groupedScopeMatchCodes: Yup.array().when('scope', {
            is: (value: string) => value === 'BASKET',
            then: schema => schema.notRequired(),
            otherwise: schema =>
                schema
                    .of(Yup.string().required('This field is required.'))
                    .min(1, 'Please enter at least one match code')
        }),
        rule: Yup.mixed().when('scope', (value, schema) =>
            value !== 'BASKET'
                ? schema
                      .oneOf([
                          'CHEAPEST',
                          'COSTLIEST',
                          'ALL_ALLOCATE',
                          'ALL',
                          'CHEAPEST_FIRST',
                          'COSTLIEST_FIRST'
                      ])
                      .required('This field is required')
                : schema.notRequired()
        ),
        childItemRule:
            showChildItemRulePerkForm(posProvider) &&
            Yup.mixed().when('scope', {
                is: (value: string) => value === 'ITEM' || value === 'ITEM_OR_CATEGORY',
                then: Yup.mixed().oneOf(['INCLUDE', 'EXCLUDE', 'INCLUDE_MATCHED']),
                otherwise: Yup.mixed().oneOf(['INCLUDE', 'EXCLUDE'])
            }),
        maxCount: Yup.number().when(['scope', 'rule'], {
            is: (scope: string, rule: string) =>
                (perkType === AwardTemplate.AD_HOC || perkType === AwardTemplate.CLUB) &&
                isDefined(scope) &&
                scope !== 'BASKET' &&
                isDefined(rule) &&
                (rule === 'CHEAPEST' || rule === 'COSTLIEST'),
            then: schema =>
                schema
                    .min(1, 'Please enter a value more than or equal to 1.')
                    .max(20, 'Please enter a value less than or equal to 20.')
                    .required('This field is required.'),
            otherwise: schema => schema.notRequired()
        })
    });

const basketQualificationValidationSchema = (isEdit: boolean) =>
    Yup.object().shape({
        // when disableBasketQualification is true, basketQualification is not required
        scope: Yup.mixed().when('disableBasketQualification', {
            is: (value: boolean) => value,
            then: schema => schema.notRequired(),
            otherwise: schema => schema.oneOf(Object.values(LoyaltyScope)).required('This field is required')
        }),
        matchCode: Yup.string().when(['scope', 'disableBasketQualification'], {
            is: (scope: LoyaltyScope, disableBasketQualification: boolean) =>
                !isEdit && scope !== LoyaltyScope.BASKET && !disableBasketQualification,
            then: schema => schema.required('This field is required'),
            otherwise: schema => schema.notRequired()
        }),
        minimumItems: Yup.number().when('disableBasketQualification', {
            is: (value: boolean) => value,
            then: schema => schema.notRequired(),
            otherwise: schema => schema.min(0, 'Please enter a value more than or equal to 0')
        }),
        minimumValue: Yup.number().when('disableBasketQualification', {
            is: (value: boolean) => value,
            then: schema => schema.notRequired(),
            otherwise: schema => schema.min(0, 'Please enter a value more than or equal to 0')
        })
    });

const dateTimeRestrictionValidationSchema = () =>
    Yup.object().shape({
        startTime: Yup.string().when('dateTimeRestictionOption', {
            is: (value: string[]) => value.includes('time'),
            then: Yup.string().required('This field is required'),
            otherwise: Yup.string().notRequired()
        }),
        endTime: Yup.string().when('dateTimeRestictionOption', {
            is: (value: string[]) => value.includes('time'),
            then: Yup.string().required('This field is required'),
            otherwise: Yup.string().notRequired()
        }),
        startDate: Yup.string().when('dateTimeRestictionOption', {
            is: (value: string[]) => value.includes('date'),
            then: Yup.string()
                .required('This field is required.')
                .test('valid-date', 'Please enter valid date', value => {
                    if (value && !isValid(new Date(value))) {
                        return false;
                    }

                    return true;
                }),
            otherwise: Yup.string().notRequired()
        }),
        endDate: Yup.string().when('dateTimeRestictionOption', {
            is: (value: string[]) => value.includes('date'),
            then: Yup.string()
                .required('This field is required.')
                .test('valid-date', 'Please enter valid date', value => {
                    if (value && !isValid(new Date(value))) {
                        return false;
                    }

                    return true;
                }),
            otherwise: Yup.string().notRequired()
        }),
        daysOfWeek: Yup.array().notRequired()
    });

export const PerkRedemptionForm: React.FC<PerkRedemptionFormProps> = ({
    onSubmit,
    initialData,
    isEdit,
    perkType,
    clickPrevious,
    onPrevious,
    isLoading,
    currencySymbol,
    auto,
    onUpdate
}) => {
    const {
        initialValues,
        isIbsAndAuto,
        posProvider,
        toggledFields,
        setToggledFields,
        handleSubmit,
        handleUpdate
    } = usePepperPerksRedemptionFormData({
        initialData,
        onSubmit,
        onUpdate,
        perkType,
        auto
    });

    const validationSchema = React.useMemo(() => {
        if (isIbsAndAuto) {
            return Yup.object().shape({
                dateTimeRestriction: dateTimeRestrictionValidationSchema()
            });
        }
        return Yup.object().shape({
            discount: discountValidationSchema(posProvider, perkType),
            basketQualification: basketQualificationValidationSchema(isEdit),
            dateTimeRestriction: dateTimeRestrictionValidationSchema()
        });
    }, [isEdit, posProvider, isIbsAndAuto, perkType]);

    return (
        <MuiForm
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            initialValues={initialValues || getAwardRedemptionDefaultFormValues(perkType, isIbsAndAuto)}
        >
            <Form>
                {isIbsAndAuto ? (
                    <RedemptionFormCardContent
                        clickPrevious={clickPrevious}
                        onUpdate={handleUpdate}
                        isLoading={isLoading}
                        onPrevious={onPrevious}
                        currencySymbol={currencySymbol}
                        perkType={perkType}
                        isEdit={isEdit}
                        auto
                    />
                ) : (
                    <RedemptionFormCardContent
                        clickPrevious={clickPrevious}
                        onUpdate={handleUpdate}
                        isLoading={isLoading}
                        onPrevious={onPrevious}
                        currencySymbol={currencySymbol}
                        perkType={perkType}
                        isEdit={isEdit}
                        toggledFields={toggledFields}
                        onToggleChange={setToggledFields}
                    />
                )}
            </Form>
        </MuiForm>
    );
};
